/**
 * Pure general-US regulatory feasibility screen.
 *
 * `screenRegulatoryFeasibility(input)` maps the minimal evidence-qualified
 * input (built by `inputs.ts` from Project Intent and frozen SED evidence) to
 * one row per issue (one grid-interface row per connection scenario). Rules:
 *
 *   - missing or unknown input is 'unresolved' ('not-yet-screened' when
 *     nothing was recorded, 'open' when inputs were screened);
 *   - declared on-site combustion (primary or backup) without a cited permit is
 *     'approval required' for air (state/local applicability determination;
 *     permit type unresolved), never a pass;
 *   - 'confirmed restriction' only from a documented effective restriction;
 *   - 'no blocker identified' only from a checked effective document source;
 *     for land use, only from a document-extracted permitted-use entry that
 *     names the use itself (generation: an electric/power use covering the
 *     declared duty and technology, never accessory/emergency-only for prime,
 *     unknown or routine duty), with no district-wide, unattributed or
 *     ambiguous condition;
 *   - a bounding-box state estimate never names an authority;
 *   - no status reads a MW figure.
 *
 * The output is validated against `regulatoryFeasibilityScreenResultSchema`,
 * so every doctrine invariant is enforced on every result. IO-free.
 */
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import {
  CONNECTION_SCENARIOS, NO_BLOCKER_QUALIFIER, REGULATORY_DOCTRINE, REGULATORY_FEASIBILITY_SCREEN_VERSION,
  REGULATORY_SCREEN_QUALIFIER, SOURCES_CHECKED_ON,
  regulatoryFeasibilityInputSchema, regulatoryFeasibilityScreenResultSchema,
  type ConnectionScenario, type DocumentedRestriction, type GenerationDuty, type RegulatoryAuthority,
  type RegulatoryFeasibilityInput, type RegulatoryFeasibilityScreenResult, type RegulatoryIssue,
  type RegulatoryIssueId, type RegulatoryPhaseInput, type RegulatorySource,
} from './types';

type Row = RegulatoryIssue;
type RowStatus = Pick<Row, 'status' | 'unresolvedReason' | 'qualifier'>;

const unresolved = (reason: 'not-yet-screened' | 'open'): RowStatus => ({ status: 'unresolved', unresolvedReason: reason });
const approvalRequired: RowStatus = { status: 'approval-required' };
const noBlocker: RowStatus = { status: 'no-blocker-identified', qualifier: NO_BLOCKER_QUALIFIER };
const confirmed: RowStatus = { status: 'confirmed-restriction' };

function orderedScenarios(values: Iterable<ConnectionScenario>): ConnectionScenario[] {
  const set = new Set(values);
  return CONNECTION_SCENARIOS.filter(scenario => set.has(scenario));
}

function sentence(...parts: Array<string | false | null | undefined>): string {
  return parts.filter((part): part is string => typeof part === 'string' && part.length > 0).join(' ');
}

interface ScreenContext {
  input: RegulatoryFeasibilityInput;
  phases: RegulatoryPhaseInput[];
  scenarios: ConnectionScenario[];
  /** Resolved state only; null when unknown or only estimated. */
  state: string | null;
  servingUtility: string | null;
  iso: string | null;
}

function context(input: RegulatoryFeasibilityInput): ScreenContext {
  const phases = [...input.phases].sort((a, b) => a.sequence - b.sequence);
  const scenarios = phases.length > 0 ? orderedScenarios(phases.map(phase => phase.connection)) : ['unknown' as const];
  return {
    input,
    phases,
    scenarios,
    // A bounding-box estimate never names an authority: a border site could
    // otherwise name the wrong state's agency.
    state: input.jurisdiction.state.provenance === 'estimate' ? null : input.jurisdiction.state.value,
    servingUtility: input.utility.documentedServingUtility?.name ?? input.utility.declaredServingUtilities[0] ?? null,
    iso: input.utility.iso.value,
  };
}

function scenariosOf(ctx: ScreenContext, phases: readonly RegulatoryPhaseInput[]): ConnectionScenario[] {
  return phases.length > 0 ? orderedScenarios(phases.map(phase => phase.connection)) : ctx.scenarios;
}

function islandedNote(scenarios: readonly ConnectionScenario[]): string | null {
  return scenarios.includes('islanded') ? REGULATORY_DOCTRINE.islandedNoExemption : null;
}

/**
 * A documented restriction for this issue. Only a document/provider basis with
 * an effective rule is binding; anything else is reported but stays open.
 */
function restrictionFor(input: RegulatoryFeasibilityInput, issue: RegulatoryIssueId): {
  binding: DocumentedRestriction | null; pending: DocumentedRestriction | null;
} {
  const rows = input.landUse.documentedRestrictions.filter(row => row.issue === issue);
  const binding = rows.find(row => (row.basis === 'document' || row.basis === 'provider') && row.source.ruleStatus === 'effective') ?? null;
  return { binding, pending: binding ? null : rows[0] ?? null };
}

function confirmedRestrictionRow(base: Omit<Row, keyof RowStatus | 'authority' | 'evidenceBasis' | 'sources' | 'implication' | 'nextAction'>,
  restriction: DocumentedRestriction, subject: string, scenarios: readonly ConnectionScenario[], ...notes: Array<string | null>): Row {
  return {
    ...base, ...confirmed,
    authority: restriction.authority,
    evidenceBasis: restriction.basis,
    sources: [restriction.source],
    implication: sentence(`A documented, effective restriction applies to ${subject}.`, ...notes, islandedNote(scenarios)),
    nextAction: `Treat the restriction as binding: confirm its scope with counsel and the issuing authority, then change the site, the ${subject} design or seek a legislative change.`,
  };
}

/** Evidence basis of a recorded label: only a document or provider counts. */
function basisOf(provenance: 'provider' | 'document' | 'derived' | 'none'): 'document' | 'provider' | 'none' {
  return provenance === 'document' || provenance === 'provider' ? provenance : 'none';
}

function pendingRestrictionNote(pending: DocumentedRestriction | null): string | null {
  if (!pending) return null;
  return pending.source.ruleStatus === 'effective'
    ? 'A restriction is reported without a document or provider basis, so it is neither confirmed nor cleared.'
    : `A ${pending.source.ruleStatus} restriction is reported; it is not an effective rule, so it is neither confirmed nor cleared.`;
}

// ── Land use ─────────────────────────────────────────────────────────────────

type LandUse = 'data-center' | 'generation';

/** A permitted-use entry that names the use itself, so it can clear it.
 * Generation needs an electric or power context: bare engines, batteries or
 * generators ('Small engine repair') never count, nor does bare co-location
 * ('Co-location of wireless antennas on existing towers'). */
const USE_TERMS: Readonly<Record<LandUse, RegExp>> = {
  // Bare 'data processing' ('Office, including data processing') names an
  // office activity, not the use: it stays in USE_MENTIONS only.
  'data-center': /\bdata[\s-]?cent(?:er|re)s?\b|\bhyperscale\b|\bco-?location\s+(?:data[\s-]?cent(?:er|re)s?|facilit(?:y|ies))\b|\bserver farms?\b|\bcomputing facilit(?:y|ies)\b/i,
  generation: /\b(?:electric(?:al|ity)?|power|energy)\s+generat(?:ion|ing)\b|\bgenerating\s+(?:facilit(?:y|ies)|stations?|plants?)\b|\bpower\s+(?:plants?|stations?)\b|\bbattery\s+(?:energy\s+)?storage\b|\bBESS\b/i,
};
/** Any mention of the use. Broader than USE_TERMS on purpose: it only
 * attributes a condition or prohibition to the use, which can make the row
 * more conservative but never clears it. */
const USE_MENTIONS: Readonly<Record<LandUse, RegExp>> = {
  'data-center': /\bdata[\s-]?cent(?:er|re)s?\b|\bhyperscale\b|\bco-?location\b|\bdata[\s-]processing\b|\bserver farms?\b|\bcomputing facilit(?:y|ies)\b/i,
  generation: /\bgenerat(?:ion|ing|ors?)\b|\bpower (?:plants?|stations?|generation)\b|\benergy (?:facilit(?:y|ies)|generation|storage)\b|\bturbines?\b|\bengines?\b|\bmicrogrids?\b|\bbatter(?:y|ies)\b|\bBESS\b/i,
};
/** An entry about a trade in the use's equipment ('Power generation
 * equipment sales and repair') does not name the use itself. */
const TRADE_USE = /\b(?:repair|sales?|retail|rental|manufactur\w*|equipment|parts|showroom|wholesale|maintenance)\b/i;
/** An exclusion inside a permitted-use entry ('Light industrial (except data
 * centers)', 'Power plant (no gas turbines)', 'Power generation, excluding
 * fossil fuel'). What follows it is excluded from the entry. */
const EXCLUSION_QUALIFIER = /\b(?:except|excluding|exclusive\s+of|not\s+including|other\s+than|without|no|non-|prohibited)\b/gi;
/** Generation wording an exclusion can name besides the use itself. */
const GENERATION_EXCLUSION_TERMS = /\bfossil\b|\bcombustion\b|\bthermal\b|\bfuel\b/i;

/** An entry whose exclusion names the use (or, for generation, a technology)
 * does not clear the use; it stays a related mention. */
function excludesUse(entry: string, use: LandUse): boolean {
  for (const match of entry.matchAll(EXCLUSION_QUALIFIER)) {
    const excluded = entry.slice((match.index ?? 0) + match[0].length);
    if (USE_MENTIONS[use].test(excluded)) return true;
    if (use === 'generation' && (GENERATION_EXCLUSION_TERMS.test(excluded)
      || TECHNOLOGY_TERMS.some(term => term.pattern.test(excluded)))) return true;
  }
  return false;
}
/** A condition stated for the whole district applies to every listed use. */
const DISTRICT_WIDE = /\b(?:every|all|any|each)\s+(?:other\s+|permitted\s+|principal\s+|listed\s+)?(?:uses?|development|buildings?|structures?|projects?)\b|\bdistrict[\s-]wide\b|\bthroughout the district\b/i;
const CONDITIONAL_CUE = /\b(?:special|conditional)[\s-]use\b|\bspecial exception\b|\bSUP\b|\bCUP\b|\brezon(?:e|ing)\b|\bvariance\b|\bspecial permit\b|\blegislative approval\b|\bwith (?:board |council )?approval\b|\brequires? (?:a |an )?(?:permit|approval|hearing)\b/i;
const PROHIBITION_CUE = /\bprohibit(?:s|ed|ion)?\b|\bnot (?:permitted|allowed)\b|\bmoratorium\b|\bbann?ed\b/i;
/** A negation that directly governs a conditional cue ("no conditional use",
 * "without a special permit", "not requiring a variance"). A negation
 * elsewhere in the sentence ("with no on-site generation require a
 * conditional use permit") does not deny the cue. A denied prohibition is not
 * read this way: it stays open (conservative). */
const NEGATION_GOVERNS = /(?:\b(?:no|without|neither|nor)|\bnot(?:\s+(?:requir(?:e|es|ed|ing)|need(?:s|ed)?|subject\s+to))?)\s+(?:(?:a|an|any)\s+)?$/i;
/** "no conditional use requirements or special permits": the governing
 * negation carries across a plain or/nor list of cues. It does not carry
 * across ', and a <new cue>' ("no rezoning, and a special use permit is
 * obtained"), which states a new requirement. */
const COORDINATED_GAP = /^(?:\s+(?:requirements?|permits?|approvals?|applications?|reviews?|hearings?))?\s*,?\s+(?:or|nor)\s+(?:(?:a|an|any)\s+)?$/i;
/** A sentence that denies a cue but still states a requirement is not read as
 * a denial: it stays open. */
const REQUIREMENT_VERB = /\brequir(?:e|es|ed|ing)\b|\bsubject\s+to\b/i;
/** A 'without' that follows an earlier negation is a double negative, i.e. a
 * requirement: "No data center shall be established without a special use permit". */
const WITHOUT_GOVERNS = /\bwithout\s+(?:(?:a|an|any)\s+)?$/i;
const EARLIER_NEGATOR = /\b(?:no|not|never|nor|neither|cannot)\b|n't\b/i;
/** Requirement or prohibition wording the cue lists may not name ('require a
 * use permit', 'only with Board of Supervisors approval', 'not a permitted
 * use', 'may not include'). In a sentence about the use it never clears. */
const REQUIREMENT_PHRASE = /\brequir(?:e|es|ed|ing)\b|\bsubject\s+to\b|\bonly\s+(?:with|if|upon|by|after|when)\b|\bnot\s+(?:be\s+)?(?:a\s+)?(?:permitted|allowed)\b|\b(?:may|shall|must)\s+not\b/i;

/** Conditional cues of one text: any cue not governed by a negation, and
 * whether a negated cue sits beside a stated requirement. */
function readConditionalCues(text: string): { conditional: boolean; deniedBesideRequirement: boolean } {
  const pattern = new RegExp(CONDITIONAL_CUE.source, 'gi');
  let conditional = false;
  let denied = false;
  let negatedEnd: number | null = null;
  for (const match of text.matchAll(pattern)) {
    const before = text.slice(0, match.index);
    const doubleNegative = WITHOUT_GOVERNS.test(before) && EARLIER_NEGATOR.test(before.replace(WITHOUT_GOVERNS, ''));
    const negated = !doubleNegative && (NEGATION_GOVERNS.test(before)
      || (negatedEnd !== null && COORDINATED_GAP.test(text.slice(negatedEnd, match.index))));
    if (negated) {
      denied = true;
      negatedEnd = match.index + match[0].length;
    } else {
      conditional = true;
      negatedEnd = null;
    }
  }
  return { conditional, deniedBesideRequirement: denied && !conditional && REQUIREMENT_VERB.test(text) };
}
const PROPOSAL_CUE = /\bproposed\b|\bpending\b|\bletter of intent\b|\bdraft\b|\bapplication (?:filed|submitted)\b/i;
/** A permitted-use entry limited to an accessory or incidental use. */
const ACCESSORY_USE = /\b(?:accessory|incidental|ancillary)\b/i;
/** A generation entry limited to accessory, emergency or standby use. */
const LIMITED_DUTY_USE = /\b(?:accessory|emergency|stand-?by|back-?up|incidental|ancillary)\b/i;
/** Technologies a permitted-use entry can name. An entry naming one clears
 * only phases built from it; an entry naming none is a generic generation use. */
const TECHNOLOGY_TERMS: ReadonlyArray<{ pattern: RegExp; components: readonly string[] }> = [
  { pattern: /\bsolar\b|\bphotovoltaic\b|\bPV\b/i, components: ['solar-bess'] },
  { pattern: /\bwind\b/i, components: ['wind-bess'] },
  { pattern: /\bnuclear\b|\breactors?\b/i, components: ['smr', 'large-nuclear'] },
  { pattern: /\bbatter(?:y|ies)\b|\benergy\s+storage\b|\bBESS\b/i, components: ['battery'] },
  { pattern: /\bgas\b|\bturbines?\b|\bcombustion\b|\breciprocating\b|\bengines?\b|\bdiesel\b/i, components: ['gas-turbine', 'recip'] },
  { pattern: /\bhydro\w*|\bgeothermal\b|\bbiomass\b|\bfuel[\s-]cells?\b|\bcoal\b|\bwaste[\s-]to[\s-]energy\b|\blandfill\b/i, components: [] },
];

function technologyCovers(entry: string, phases: readonly RegulatoryPhaseInput[]): boolean {
  const named = TECHNOLOGY_TERMS.filter(term => term.pattern.test(entry));
  if (named.length === 0) return true;
  const covered = new Set(named.flatMap(term => term.components));
  return phases.every(phase => phase.components.every(component => component === 'grid' || covered.has(component)));
}

interface ZoningReading {
  /** A permitted-use entry names the use itself. */
  mentioned: boolean;
  /** Only a related use is listed (e.g. generators, engines, co-location, equipment repair). */
  relatedOnly: boolean;
  /** A listed entry covers the declared use (generation: its duty and technology). */
  clears: boolean;
  /** Why a listed entry does not cover the declared use: accessory-only
   * data-center use, or generation limited in duty or technology. */
  limitation: 'accessory' | 'duty' | 'technology' | null;
  /** An accessory/emergency/standby entry is listed for non-emergency or routine generation. */
  limitedDutyListed: boolean;
  conditional: boolean;
  /** A condition not tied to a named use, or a denied cue beside a requirement. */
  ambiguous: boolean;
  prohibitionMentioned: boolean;
  ruleStatus: RegulatorySource['ruleStatus'];
}

function readZoningDocument(doc: NonNullable<RegulatoryFeasibilityInput['landUse']['zoningDocument']>, use: LandUse,
  generationPhases: readonly RegulatoryPhaseInput[]): ZoningReading {
  const other: LandUse = use === 'data-center' ? 'generation' : 'data-center';
  const entries = doc.permittedUses.filter(entry => USE_TERMS[use].test(entry) && !TRADE_USE.test(entry) && !excludesUse(entry, use));
  const related = doc.permittedUses.filter(entry => USE_MENTIONS[use].test(entry));
  // Emergency-standby generation that serves load routinely (islanded or
  // without grid supply) is not emergency use: an emergency listing never covers it.
  const emergencyOnly = generationPhases.length > 0
    && generationPhases.every(phase => phase.duty === 'emergency-standby' && !routineGeneration(phase));
  const covered = use === 'data-center'
    ? (entry: string) => !ACCESSORY_USE.test(entry)
    : (entry: string) => !LIMITED_DUTY_USE.test(entry) || emergencyOnly;
  const clearing = entries.filter(entry => covered(entry) && (use === 'data-center' || technologyCovers(entry, generationPhases)));

  // Cues in an entry about this use, or in a district-wide entry ('Any use by special use permit').
  const cueEntries = doc.permittedUses.filter(entry => USE_MENTIONS[use].test(entry) || DISTRICT_WIDE.test(entry));
  let conditional = cueEntries.some(entry => readConditionalCues(entry).conditional);
  let ambiguous = cueEntries.some(entry => readConditionalCues(entry).deniedBesideRequirement);
  let prohibitionMentioned = cueEntries.some(entry => PROHIBITION_CUE.test(entry));
  for (const sentence of (doc.specialConditions ?? '').split(/(?<=[.;])\s+|\n+/)) {
    const attributed = USE_MENTIONS[use].test(sentence) || DISTRICT_WIDE.test(sentence);
    // A sentence naming only the other screened use does not reach this one.
    if (!attributed && USE_MENTIONS[other].test(sentence)) continue;
    const cues = readConditionalCues(sentence);
    const prohibition = PROHIBITION_CUE.test(sentence);
    if (attributed) {
      conditional ||= cues.conditional;
      prohibitionMentioned ||= prohibition;
      ambiguous ||= cues.deniedBesideRequirement || (!cues.conditional && REQUIREMENT_PHRASE.test(sentence));
    } else {
      // A cue that names no use may still govern this one: never a clearance.
      ambiguous ||= cues.conditional || prohibition || cues.deniedBesideRequirement;
    }
  }
  return {
    mentioned: entries.length > 0,
    relatedOnly: entries.length === 0 && related.length > 0,
    clears: clearing.length > 0,
    limitation: entries.length === 0 || clearing.length > 0 ? null
      : entries.some(covered) ? 'technology' : use === 'data-center' ? 'accessory' : 'duty',
    limitedDutyListed: use === 'generation' && !emergencyOnly && related.some(entry => LIMITED_DUTY_USE.test(entry)),
    conditional,
    ambiguous,
    prohibitionMentioned,
    ruleStatus: PROPOSAL_CUE.test(`${doc.zoningCode ?? ''} ${doc.specialConditions ?? ''}`) ? 'proposed' : 'effective',
  };
}

/** Why a listed or mentioned use still does not clear the row. */
function zoningOpenReason(reading: ZoningReading, subject: string, basis: 'document' | 'declared'): string {
  if (!reading.mentioned) {
    return reading.relatedOnly
      ? `The zoning record lists only a related use, not ${subject} itself, so it does not clear the use.`
      : `The zoning record does not list ${subject}; a zoning designation alone does not establish permission.`;
  }
  if (basis === 'declared' || reading.ruleStatus !== 'effective') {
    return `The zoning record lists ${subject}, but the source is ${basis === 'declared' ? 'user-recorded' : `a ${reading.ruleStatus} rule`}, so it does not clear the use.`;
  }
  if (reading.ambiguous) {
    return `The zoning record carries a condition that names no use, or denies an approval while stating a requirement, so it does not clear ${subject}.`;
  }
  return reading.limitation === 'accessory'
    ? `The zoning record lists ${subject} only as an accessory or incidental use, so it does not clear the use.`
    : reading.limitation === 'duty'
      ? `The zoning record lists ${subject} only as accessory, emergency or standby use, which does not cover the declared operating duty or routine operation.`
      : `The zoning record lists ${subject} only for a different generation technology than the one declared, so it does not clear the use.`;
}

/** Public provider labels are quoted; a label read from an uploaded document
 * is named without its text so no private document wording leaves the report. */
function labelSource(name: string, provenance: 'provider' | 'document' | 'derived' | 'none', value: string): string {
  return provenance === 'provider' ? `${name} (provider): ${value}` : `${name} (${provenance})`;
}

function landUseAuthority(ctx: ScreenContext, documented: boolean): RegulatoryAuthority {
  const name = ctx.input.jurisdiction.localAuthority.candidateName;
  return documented ? { level: 'local', name } : { level: 'unknown', name };
}

function landUseRow(ctx: ScreenContext, use: 'data-center' | 'generation'): Row {
  const issue: RegulatoryIssueId = use === 'data-center' ? 'land-use-data-center' : 'land-use-generation';
  const subject = use === 'data-center' ? 'data-center use' : 'on-site generation use';
  const generationPhases = ctx.phases.filter(phase => phase.generation);
  const phases = use === 'data-center' ? ctx.phases : generationPhases;
  const scenarios = scenariosOf(ctx, phases);
  const base = { id: issue, issue, affectedScenarios: scenarios, phaseIds: phases.map(phase => phase.phaseId), reverifyAtReport: true as const };
  const { binding, pending } = restrictionFor(ctx.input, issue);
  if (binding) return confirmedRestrictionRow(base, binding, subject, scenarios);

  const separation = use === 'data-center'
    ? 'Data-center use permission is separate from generation use permission.'
    : 'Generation use is separate from data-center use; noise, setback, fuel-storage and fire/building conditions can apply, including to standby generators.';
  const authorityNote = 'The permitting authority was not evaluated; the jurisdiction screen is geographic only.';
  const doc = ctx.input.landUse.zoningDocument;
  const zoning = ctx.input.landUse.parcelZoning;
  const sources: RegulatorySource[] = [];
  if (zoning.value) sources.push({ id: 'parcel-zoning', label: labelSource('Parcel zoning label', zoning.provenance, zoning.value), asOf: zoning.asOf, ruleStatus: 'unknown' });

  if (use === 'generation' && generationPhases.length === 0) {
    const anyRecorded = ctx.phases.some(phase => phase.components.length > 0);
    return {
      ...base, ...unresolved(anyRecorded ? 'open' : 'not-yet-screened'),
      authority: landUseAuthority(ctx, false), evidenceBasis: anyRecorded ? 'declared' : 'none', sources,
      implication: sentence(anyRecorded
        ? 'Only grid supply is recorded; on-site generation, including emergency or standby generators, is not recorded, so generation use was not screened.'
        : 'No on-site generation or storage is recorded, so generation use was not screened.', separation, islandedNote(scenarios)),
      nextAction: 'Record whether any on-site generation or storage is planned, including emergency or standby generators, then obtain a use determination for it from the planning authority.',
    };
  }

  if (doc) {
    const reading = readZoningDocument(doc, use, generationPhases);
    const docSource: RegulatorySource = {
      id: 'zoning-document',
      label: doc.tier === 'document' ? 'Zoning document on file (permitted uses)' : 'User-recorded zoning details (permitted uses)',
      asOf: null,
      ruleStatus: doc.tier === 'document' ? reading.ruleStatus : 'unknown',
    };
    const documentSources = [docSource, ...sources];
    const basis = doc.tier === 'document' ? 'document' as const : 'declared' as const;
    const authority = landUseAuthority(ctx, true);
    if (pending || reading.prohibitionMentioned) {
      return {
        ...base, ...unresolved('open'), authority, evidenceBasis: basis, sources: documentSources,
        implication: sentence(pendingRestrictionNote(pending)
          ?? `The zoning record mentions a possible restriction on ${subject}; free text is not read as a confirmed restriction or as a clearance.`, separation, islandedNote(scenarios)),
        nextAction: `Obtain the planning authority's written determination of whether ${subject} is permitted on this parcel and under which ordinance section.`,
      };
    }
    if (reading.conditional) {
      return {
        ...base, ...approvalRequired, authority, evidenceBasis: basis, sources: documentSources,
        implication: sentence(`The zoning record ties ${subject} to a discretionary approval (special or conditional use, rezoning or variance).`, separation, islandedNote(scenarios), authorityNote),
        nextAction: `Confirm the required approval for ${subject}, its hearing process and timeline with the local planning authority.`,
      };
    }
    if (reading.clears && !reading.ambiguous && basis === 'document' && reading.ruleStatus === 'effective') {
      return {
        ...base, ...noBlocker, authority, evidenceBasis: basis, sources: documentSources,
        implication: sentence(`The zoning document on file lists ${subject} among permitted uses.`, separation, islandedNote(scenarios)),
        nextAction: `Reverify the permitted-use basis against the current ordinance at report time; site-plan, building and fire approvals for ${subject} still apply.`,
      };
    }
    return {
      ...base, ...unresolved('open'), authority, evidenceBasis: basis, sources: documentSources,
      implication: sentence(zoningOpenReason(reading, subject, basis),
        reading.limitedDutyListed ? REGULATORY_DOCTRINE.emergencyNotPrime : null, separation, islandedNote(scenarios), authorityNote),
      nextAction: `Obtain a zoning verification letter or planning-department use determination that addresses ${subject} for this parcel.`,
    };
  }

  const landUse = ctx.input.landUse.parcelLandUse;
  const labelled = zoning.value != null || landUse.value != null;
  if (landUse.value) sources.push({ id: 'parcel-land-use', label: labelSource('Parcel land-use label', landUse.provenance, landUse.value), asOf: landUse.asOf, ruleStatus: 'unknown' });
  return {
    ...base, ...unresolved(labelled || pending ? 'open' : 'not-yet-screened'),
    authority: landUseAuthority(ctx, false), evidenceBasis: basisOf(zoning.value != null ? zoning.provenance : landUse.provenance),
    sources,
    implication: sentence(pendingRestrictionNote(pending), labelled
      ? `A parcel zoning or land-use label is recorded, but a label alone does not establish ${subject} permission.`
      : `No zoning evidence is recorded for ${subject}.`, separation, islandedNote(scenarios), authorityNote),
    nextAction: `Obtain a zoning verification letter or planning-department use determination that addresses ${subject} for this parcel.`,
  };
}

// ── Air permit and operating duty ────────────────────────────────────────────

/** One duty for the air row: the shared recorded duty, 'unknown' when none is
 * recorded, else 'mixed' (a partly recorded set is not one known duty). */
function aggregateDuty(phases: readonly RegulatoryPhaseInput[]): GenerationDuty {
  const duties = new Set(phases.map(phase => phase.duty));
  const known = [...duties].filter(duty => duty !== 'unknown');
  if (known.length === 0) return 'unknown';
  return known.length === 1 && !duties.has('unknown') ? known[0]! : 'mixed';
}

/** Generation serves load routinely when the phase is islanded or has no
 * grid supply component. */
function routineGeneration(phase: RegulatoryPhaseInput): boolean {
  return phase.connection === 'islanded' || !phase.components.includes('grid');
}

const POLLUTANT_LABEL: Readonly<Record<'ozone' | 'pm2.5' | 'pm10' | 'unspecified', string>> = {
  ozone: 'ozone', 'pm2.5': 'fine particulate (PM2.5)', pm10: 'PM-10', unspecified: 'an unspecified pollutant',
};

function airRow(ctx: ScreenContext): Row {
  const combustionPhases = ctx.phases.filter(phase => phase.combustion);
  const scenarios = scenariosOf(ctx, combustionPhases);
  const duty = aggregateDuty(combustionPhases);
  const epa = ctx.input.air.epa;
  const sources: RegulatorySource[] = epa.status === 'not-assessed' ? [] : [{
    id: 'epa-green-book', label: 'EPA Green Book NAAQS designations (county listing)', url: 'https://www.epa.gov/green-book',
    asOf: epa.asOf, ruleStatus: 'effective',
  }];
  const authority: RegulatoryAuthority = { level: 'state', name: ctx.state ? `${ctx.state} state or local air permitting agency` : null };
  const base = {
    id: 'air-permit-operating-duty', issue: 'air-permit-operating-duty' as const, affectedScenarios: scenarios,
    phaseIds: combustionPhases.map(phase => phase.phaseId), permitStage: 'unknown' as const, duty, reverifyAtReport: true as const,
  };
  const { binding, pending } = restrictionFor(ctx.input, 'air-permit-operating-duty');
  if (binding) {
    return { ...confirmedRestrictionRow(base, binding, 'on-site combustion', scenarios,
      duty === 'emergency-standby' || duty === 'mixed' ? REGULATORY_DOCTRINE.emergencyNotPrime : null), permitStage: 'unknown', duty };
  }

  const epaNote = epa.status === 'nonattainment'
    ? `EPA lists ${POLLUTANT_LABEL[epa.pollutant]} nonattainment${epa.classification ? ` (${epa.classification})` : ''}${epa.siteContainment === 'outside' ? ' in part of the county, with the site point outside the mapped area' : ''}; nonattainment new source review can apply depending on potential to emit.`
    : epa.status === 'not-listed'
      ? 'EPA lists no current nonattainment for the county; that does not clear PSD, minor-source or state permits.'
      : 'EPA attainment status was not assessed.';
  const otrNote = ctx.input.air.ozoneTransportRegion === 'inside'
    ? 'The site is in the Ozone Transport Region, where NOx and VOC requirements apply regardless of attainment.' : null;

  if (combustionPhases.length === 0) {
    return {
      ...base, ...unresolved('not-yet-screened'), authority, evidenceBasis: 'none', sources,
      implication: sentence('No on-site combustion (primary or emergency/standby) is recorded, so air-permit applicability was not screened; data centers commonly carry standby generators.',
        pendingRestrictionNote(pending), epaNote, otrNote, islandedNote(scenarios)),
      nextAction: 'Record whether any on-site combustion is planned, including emergency or standby generators, with fuel, equipment and operating duty; then request the state or local air agency\'s applicability determination.',
    };
  }

  const emergencyRoutine = combustionPhases.filter(phase => phase.duty === 'emergency-standby' && routineGeneration(phase));
  const dutyNote = emergencyRoutine.length > 0
    ? `Emergency-standby duty is recorded for generation that would serve load routinely (islanded or without grid supply); ${REGULATORY_DOCTRINE.emergencyNotPrime} Routine operation needs a permit envelope covering that duty.`
    : duty === 'emergency-standby' || duty === 'mixed'
      ? `${REGULATORY_DOCTRINE.emergencyNotPrime} Each duty needs a permit envelope that covers it.`
      : duty === 'prime-continuous' || duty === 'peaking'
        ? 'Prime or peaking duty needs an operating permit envelope covering the proposed operation and its enforceable limits.'
        : 'Operating duty is not recorded, so the permit envelope cannot be scoped.';
  return {
    ...base, ...(pending ? unresolved('open') : approvalRequired), authority, evidenceBasis: 'declared', sources,
    implication: sentence('On-site combustion is declared; a state or local air-agency applicability determination is needed and the permit type is unresolved.',
      pendingRestrictionNote(pending), REGULATORY_DOCTRINE.noMwSafeHarbour,
      'Applicability depends on equipment, fuel, operating duty and enforceable limits (potential to emit).',
      epaNote, otrNote, dutyNote, islandedNote(scenarios)),
    nextAction: `Request the state or local air agency's applicability determination covering equipment, fuel, operating duty and enforceable limits; confirm whether construction and operating authorizations are separate and that any permit covers the proposed operating envelope.`,
  };
}

// ── Ownership and electricity sale ──────────────────────────────────────────

function ownershipRow(ctx: ScreenContext): Row {
  const supply = ctx.input.electricitySupply;
  const generationPhases = ctx.phases.filter(phase => phase.generation);
  const scenarios = ctx.scenarios;
  const authority: RegulatoryAuthority = { level: 'state', name: ctx.state ? `${ctx.state} public utility commission` : null };
  const base = { id: 'ownership-electricity-sale', issue: 'ownership-electricity-sale' as const, affectedScenarios: scenarios,
    phaseIds: generationPhases.map(phase => phase.phaseId), reverifyAtReport: true as const };
  const { binding, pending } = restrictionFor(ctx.input, 'ownership-electricity-sale');
  if (binding) return confirmedRestrictionRow(base, binding, 'the proposed ownership or sale structure', scenarios);
  const stateNote = ctx.state ? null : 'The site state is not resolved, so the governing sale rules are unknown.';
  const statement = supply.arrangement === 'own-use'
    ? 'Own-use self-generation is declared. Whether a separate owner, affiliate or project company supplying the operator counts as a sale depends on state law and the utility\'s service territory.'
    : supply.arrangement === 'third-party-onsite-sale'
      ? 'A separate company would sell on-site electricity to the operator. Third-party onsite sales are permitted, restricted or prohibited depending on state law and utility service-territory rights.'
      : supply.arrangement === 'utility-supplied'
        ? 'Utility supply is declared; any on-site generation must stay within own-use or utility-approved arrangements.'
        : supply.recordedUnknown
          ? 'The ownership and electricity-sale arrangement is recorded as unknown.'
          : 'No ownership or electricity-sale arrangement is recorded.';
  const screened = supply.arrangement !== 'unknown' || supply.recordedUnknown;
  return {
    ...base, ...unresolved(screened || pending ? 'open' : 'not-yet-screened'), authority,
    evidenceBasis: supply.arrangement !== 'unknown' ? 'declared' : 'none', sources: [],
    implication: sentence(statement, pendingRestrictionNote(pending), stateNote, islandedNote(scenarios)),
    nextAction: 'Obtain a state-law review of the proposed ownership and sale structure (state utility commission rules and the serving utility\'s territory rights) before relying on it.',
  };
}

// ── Grid interface (one row per scenario) ───────────────────────────────────

function utilitySources(ctx: ScreenContext): RegulatorySource[] {
  const documented = ctx.input.utility.documentedServingUtility;
  return documented ? [{ id: 'utility-document', label: `Utility document naming the serving utility: ${documented.name}`, asOf: documented.asOf, ruleStatus: 'unknown' }] : [];
}

function gridInterfaceRows(ctx: ScreenContext): Row[] {
  const { binding, pending } = restrictionFor(ctx.input, 'grid-interface');
  const laterGridPhase = ctx.phases.some(phase => phase.connection !== 'islanded');
  return ctx.scenarios.map((scenario): Row => {
    const phases = ctx.phases.filter(phase => phase.connection === scenario);
    const base = { id: `grid-interface:${scenario}`, issue: 'grid-interface' as const, affectedScenarios: [scenario],
      phaseIds: phases.map(phase => phase.phaseId), reverifyAtReport: true as const };
    if (binding && scenario !== 'islanded') {
      return confirmedRestrictionRow(base, binding, 'the grid interface', [scenario],
        scenario === 'grid-parallel-non-export' ? REGULATORY_DOCTRINE.zeroExportStillReviewed : null);
    }
    const utilityAuthority: RegulatoryAuthority = ctx.servingUtility
      ? { level: 'utility', name: ctx.servingUtility }
      : ctx.iso ? { level: 'rto-iso', name: ctx.iso } : { level: 'unknown', name: null };
    const sources = utilitySources(ctx);
    const restrictionNote = pendingRestrictionNote(pending);
    switch (scenario) {
      case 'islanded':
        return {
          ...base, ...unresolved('open'), authority: utilityAuthority, evidenceBasis: 'declared', sources,
          implication: sentence('Islanded operation is declared: no utility interconnection is recorded for these phases. Islanding avoids the grid interface only while there is no parallel operation, standby service or later connection.',
            laterGridPhase ? 'Another phase connects to the grid; interconnection review applies before that phase.' : null,
            restrictionNote, REGULATORY_DOCTRINE.btmCanBeGridParallel),
          nextAction: 'Confirm the design has no utility tie, parallel operation or standby service; any later connection needs utility (and, if exporting, RTO/ISO) interconnection review.',
        };
      case 'grid-parallel-non-export':
        return {
          ...base, ...(ctx.servingUtility || ctx.iso ? (restrictionNote ? unresolved('open') : approvalRequired) : unresolved('open')),
          authority: utilityAuthority, evidenceBasis: 'declared', sources,
          implication: sentence(REGULATORY_DOCTRINE.zeroExportStillReviewed,
            'Parallel operation needs the serving utility\'s interconnection review and protection requirements, such as non-export relaying.',
            ctx.servingUtility || ctx.iso ? null : 'The serving utility is not identified.', restrictionNote),
          nextAction: 'Apply to the serving utility for parallel-operation (non-export) interconnection review; confirm protection, study scope and timing.',
        };
      case 'grid-parallel-export': {
        const exportAuthority: RegulatoryAuthority = ctx.iso ? { level: 'rto-iso', name: ctx.iso } : utilityAuthority;
        return {
          ...base, ...(ctx.servingUtility || ctx.iso ? (restrictionNote ? unresolved('open') : approvalRequired) : unresolved('open')),
          authority: exportAuthority, evidenceBasis: 'declared', sources,
          implication: sentence('Exporting generation needs generator interconnection with the transmission provider or RTO/ISO and a lawful sale or market arrangement.',
            REGULATORY_DOCTRINE.generatorInterconnectionNotLoadService, REGULATORY_DOCTRINE.fercLargeLoadProceeding,
            ctx.servingUtility || ctx.iso ? null : 'The serving utility and RTO/ISO are not identified.', restrictionNote),
          nextAction: 'Enter the transmission provider or RTO/ISO generator interconnection process and keep the load-service request separate.',
        };
      }
      case 'unknown': {
        const gridConnected = phases.some(phase => phase.gridConnectionDeclared === 'grid-connected');
        const recordedUnknown = phases.some(phase => phase.connectionRecordedUnknown);
        return {
          ...base, ...unresolved(gridConnected || recordedUnknown || pending ? 'open' : 'not-yet-screened'), authority: utilityAuthority,
          evidenceBasis: gridConnected ? 'declared' : 'none', sources,
          implication: sentence(gridConnected
            ? 'A grid connection is declared but its export mode is not recorded.'
            : recordedUnknown ? 'The connection mode is recorded as unknown.' : 'The connection mode is not recorded.', REGULATORY_DOCTRINE.zeroExportStillReviewed,
          REGULATORY_DOCTRINE.generatorInterconnectionNotLoadService, REGULATORY_DOCTRINE.btmCanBeGridParallel, restrictionNote),
          nextAction: 'Record for each phase whether it runs islanded, grid-parallel without export, or exporting, then confirm the interconnection path with the serving utility.',
        };
      }
    }
  });
}

// ── Grid service and economics ───────────────────────────────────────────────

const SERVICE_TERMS = 'import capacity during on-site generation outages, standby or backup service charges, upgrade and curtailment obligations, and timing';

function gridServiceRow(ctx: ScreenContext): Row {
  const gridPhases = ctx.phases.filter(phase => phase.connection !== 'islanded');
  const allIslanded = ctx.phases.length > 0 && gridPhases.length === 0;
  const scenarios = allIslanded ? ['islanded' as const] : scenariosOf(ctx, gridPhases).filter(scenario => scenario !== 'islanded');
  const authority: RegulatoryAuthority = ctx.servingUtility ? { level: 'utility', name: ctx.servingUtility } : { level: 'unknown', name: null };
  const base = { id: 'grid-service-economics', issue: 'grid-service-economics' as const, affectedScenarios: scenarios.length > 0 ? scenarios : ['unknown' as const],
    phaseIds: gridPhases.map(phase => phase.phaseId), reverifyAtReport: true as const };
  const { binding, pending } = restrictionFor(ctx.input, 'grid-service-economics');
  if (binding) return confirmedRestrictionRow(base, binding, 'grid service', base.affectedScenarios);
  const capacity = ctx.input.gridService.capacity;
  const capacitySource: RegulatorySource[] = capacity.basis === 'none' ? [] : [{
    id: 'utility-capacity-statement',
    label: capacity.basis === 'utility-indication' ? 'Utility service letter (indication, not a commitment)'
      : capacity.basis === 'study' ? 'Utility interconnection study' : 'Executed utility agreement',
    asOf: capacity.asOf, ruleStatus: 'unknown',
  }];
  const sources = [...capacitySource, ...utilitySources(ctx)];
  const nextAction = `Ask the serving utility for a will-serve letter or service agreement that states firm import capacity (including during on-site generation outages), standby or backup charges, upgrade and curtailment obligations, and energization timing.`;
  const restrictionNote = pendingRestrictionNote(pending);
  if (allIslanded) {
    return {
      ...base, ...unresolved('open'), authority, evidenceBasis: 'declared', sources,
      implication: sentence('No utility service is declared; import capacity during on-site generation outages is unavailable unless a separate service arrangement is made.', restrictionNote),
      nextAction: 'Confirm on-site reserve and outage coverage without grid import, or record the standby service arrangement and its terms.',
    };
  }
  if (capacity.basis === 'study' && !pending) {
    return {
      ...base, ...approvalRequired, authority, evidenceBasis: 'document', sources,
      implication: sentence('A utility study is recorded; the load-service agreement and any upgrades must still be approved and executed.', `Confirm ${SERVICE_TERMS}.`),
      nextAction,
    };
  }
  const loadService = ctx.input.gridService.loadServiceStatus;
  const capacityNote = capacity.basis === 'executed'
    ? `An executed utility agreement is recorded; ${SERVICE_TERMS} still need confirmation.`
    : capacity.basis === 'utility-indication'
      ? `A utility indication is recorded; it is not a commitment. ${REGULATORY_DOCTRINE.unknownGridRightsNotEntitlement}`
      : capacity.basis === 'study'
        ? 'A utility study is recorded.'
        : REGULATORY_DOCTRINE.unknownGridRightsNotEntitlement;
  const serviceNote = loadService.value === 'executed-LGIA'
    ? `An executed generator interconnection agreement is declared. ${REGULATORY_DOCTRINE.generatorInterconnectionNotLoadService}`
    : loadService.value === 'executed-load-service'
      ? 'An executed load-service agreement is declared but not evidenced in the site record.'
      : loadService.value === 'pre-application' || loadService.value === 'queued' || loadService.value === 'study'
        ? 'A load-service or interconnection request is declared as in progress.'
        : null;
  const screened = capacity.basis !== 'none' || (loadService.value !== 'unknown' && loadService.value !== 'none') || loadService.recordedUnknown;
  return {
    ...base, ...unresolved(screened || pending ? 'open' : 'not-yet-screened'), authority,
    evidenceBasis: capacity.basis !== 'none' ? 'document' : serviceNote ? 'declared' : 'none', sources,
    implication: sentence(capacityNote, serviceNote, capacity.basis === 'executed' ? null : `Confirm ${SERVICE_TERMS}.`, restrictionNote),
    nextAction,
  };
}

/** Run the screen. Throws when the input or any produced row breaks a contract. */
export function screenRegulatoryFeasibility(rawInput: RegulatoryFeasibilityInput): RegulatoryFeasibilityScreenResult {
  const ctx = context(regulatoryFeasibilityInputSchema.parse(rawInput));
  const issues: Row[] = [
    landUseRow(ctx, 'data-center'),
    landUseRow(ctx, 'generation'),
    airRow(ctx),
    ownershipRow(ctx),
    ...gridInterfaceRows(ctx),
    gridServiceRow(ctx),
  ];
  return regulatoryFeasibilityScreenResultSchema.parse({
    schemaVersion: 1,
    screenVersion: REGULATORY_FEASIBILITY_SCREEN_VERSION,
    sourcesCheckedOn: SOURCES_CHECKED_ON,
    reverifyAtReport: true,
    qualifier: REGULATORY_SCREEN_QUALIFIER,
    noBlockerQualifier: NO_BLOCKER_QUALIFIER,
    issues,
  });
}

/** Canonical identity of a screen input or result. */
export function hashRegulatoryFeasibilityValue(value: RegulatoryFeasibilityInput | RegulatoryFeasibilityScreenResult): string {
  return hashCanonicalJson(value);
}
